import * as yup from 'yup';
import { taxOptimiserV2Events } from '@helpers/enums/gaEventNameEnum';
import { moengageEventConstants } from '@services/moengage';

// This should be in sequence with the order of the questionnaire
export const TaxQuestionnaireSlugs = {
    basicDetails: 'basicDetails',
    personalProfile: 'personalProfile',
    financialProfile: 'financialProfile',
    investmentProfiles: 'investmentProfiles',
    otherInvestmentProducts: 'otherInvestmentProducts',
};

// This should be in sequence with the order of the questionnaire
export const TaxQuestionnaireSlugsArray = Object.values(TaxQuestionnaireSlugs);

export const getQuestionnaireDetails = (slug, width) => {
    const details = {
        basicDetails: {
            title: 'Basic Questions',
            description: 'A few quick questions to understand your tax profile',
            questions: [
                {
                    id: 'professionType',
                    question:
                        'Are you a salaried professional or a business owner?',
                    options: [
                        {
                            displayName: 'Salaried Professional',
                            value: 'salariedProfessional',
                        },
                        {
                            displayName: 'Business Owner',
                            value: 'businessOwner',
                        },
                    ],
                },
                {
                    id: 'taxRegime',
                    question: 'What tax regime are you currently on?',
                    options: [
                        { displayName: 'New Regime', value: 'newRegime' },
                        { displayName: 'Old Regime', value: 'oldRegime' },
                    ],
                },
                {
                    id: 'taxKnowledge',
                    question: 'How tax savvy do you think you are?',
                    options: [
                        { displayName: 'Tax aware', value: 'taxAware' },
                        { displayName: 'Tax informed', value: 'taxInformed' },
                        { displayName: 'Tax expert', value: 'taxExpert' },
                    ],
                },
                {
                    id: 'effectiveTaxRate',
                    question: 'What is your effective tax rate?',
                    options: [
                        { displayName: '0%-10%', value: 'zeroToTen' },
                        { displayName: '10%-20%', value: 'tenToTwenty' },
                        { displayName: '20%-30%', value: 'twentyToThirty' },
                        { displayName: '>30%', value: 'aboveThirty' },
                        { displayName: "I don't know", value: 'dontKnow' },
                    ],
                },
                {
                    id: 'usesCA',
                    question:
                        'Do you take the help of a CA to file your taxes?',
                    options: [
                        { displayName: 'Yes', value: true },
                        { displayName: 'No', value: false },
                    ],
                },
            ],
        },
        personalProfile: {
            title: 'Personal Profile',
            description: 'Tell us about your family',
            questions: [
                {
                    id: 'married',
                    question: 'Are you married?',
                    options: [
                        { displayName: 'Yes', value: true },
                        { displayName: 'No', value: false },
                    ],
                },
                {
                    id: 'haveKids',
                    question: 'Do you have children?',
                    options: [
                        { displayName: 'Yes', value: true },
                        { displayName: 'No', value: false },
                    ],
                },
                {
                    id: 'haveGirlChild',
                    question: 'Do you have a girl child?',
                    options: [
                        { displayName: 'Yes', value: true },
                        { displayName: 'No', value: false },
                    ],
                    parentQuestion: {
                        id: 'haveKids',
                        value: true,
                    },
                },
                {
                    id: 'paySchoolFees',
                    question: 'Do you pay school fees?',
                    options: [
                        { displayName: 'Yes', value: true },
                        { displayName: 'No', value: false },
                    ],
                    parentQuestion: {
                        id: 'haveKids',
                        value: true,
                    },
                },
                {
                    id: 'planningHigherEducation',
                    question:
                        "Are you actively planning for your child's higher education?",
                    options: [
                        { displayName: 'Yes', value: true },
                        { displayName: 'No', value: false },
                    ],
                    parentQuestion: {
                        id: 'haveKids',
                        value: true,
                    },
                },
                {
                    id: 'otherInvestments',
                    question:
                        'Do you have other investment vehicles (e.g. HUF, Partnership)?',
                    options: [
                        { displayName: 'Yes', value: true },
                        { displayName: 'No', value: false },
                    ],
                    tooltip: {
                        content : 'Hindu Undivided Family (HUF) and Partnership investments have different tax structures and benefits - HUF gets separate tax exemptions while partnership income flows through to partners.',
                        className: 'top-7 xl:top-6 left-1/2 -translate-x-1/2 xl:-translate-x-0',
                    }
                },
                {
                    id: 'parentsSeniorCitizens',
                    question: 'Are your parents senior citizens?',
                    options: [
                        { displayName: 'Yes', value: true },
                        { displayName: 'No', value: false },
                    ],
                    conditionalRendering: {
                        section: 'basicDetails',
                        question: 'taxRegime',
                        value: 'oldRegime',
                    },
                },
            ],
        },
        financialProfile: {
            title: 'Financial Profile',
            description: 'Tell us about your income, deductions, and financial investments',
            questions: [
                {
                    id: 'lifeInsurance',
                    question: 'Do you have Life Insurance?',
                    options: [
                        { displayName: 'Yes', value: true },
                        { displayName: 'No', value: false },
                    ],
                    conditionalRendering: {
                        section: 'basicDetails',
                        question: 'taxRegime',
                        value: 'oldRegime',
                    },
                },
                {
                    id: 'medicalInsuranceSelf',
                    question:
                        'Do you claim tax deductions under Medical Insurance for yourself?',
                    options: [
                        { displayName: 'Yes', value: true },
                        { displayName: 'No', value: false },
                    ],
                    conditionalRendering: {
                        section: 'basicDetails',
                        question: 'taxRegime',
                        value: 'oldRegime',
                    },
                },
                {
                    id: 'medicalInsuranceParents',
                    question:
                        'Do you claim tax deductions under Medical Insurance for your parents?',
                    options: [
                        { displayName: 'Yes', value: true },
                        { displayName: 'No', value: false },
                    ],
                    conditionalRendering: {
                        section: 'basicDetails',
                        question: 'taxRegime',
                        value: 'oldRegime',
                    },
                },
                {
                    id: 'section80cDeduction',
                    question: 'Do you claim deductions under section 80(C)?',
                    options: [
                        { displayName: 'Yes', value: true },
                        { displayName: 'No', value: false },
                    ],
                    conditionalRendering: {
                        section: 'basicDetails',
                        question: 'taxRegime',
                        value: 'oldRegime',
                    },
                    tooltip: {
                        content : 'Section 80(C) allows deductions up to ₹1.5 lakh for investments in PPF, ELSS, life insurance premiums, and other qualifying instruments.',
                        className: `top-7 xl:top-6 left-1/2 ${width < 432 ? '-translate-x-[10%]' : '-translate-x-[90%]'} md:-translate-x-[50%] xl:-translate-x-0`,
                    }
                },
                {
                    id: 'section80cMode',
                    question:
                        'What is your mode of claiming deductions under section 80(C)?',
                    options: [
                        {
                            displayName: 'Mutual Fund (ELSS)',
                            value: 'mutualFundElss',
                        },
                        {
                            displayName: 'Tax Saving Fixed Deposit',
                            value: 'taxSavingFixedDeposit',
                        },
                        {
                            displayName: 'Provident Fund',
                            value: 'providentFund',
                        },
                        {
                            displayName: 'Combination of above',
                            value: 'combinationOfAbove',
                        },
                        { displayName: 'Others', value: 'others' },
                    ],
                    tooltip: {
                        content : 'Choose your main tax-saving method: ELSS (Equity Linked Savings Scheme) offers market returns with 3-year lock-in, Tax Saving FD provides guaranteed returns, Provident Fund includes EPF/PPF with long-term benefits, or select \'Combination\' if you use multiple options.',
                        className: 'top-7 xl:top-6 left-[60%] -translate-x-[70%] xl:-translate-x-0',
                    },
                    parentQuestion: {
                        id: 'section80cDeduction',
                        value: true,
                    },
                },
                {
                    id: 'npsInvestment',
                    question:
                        'Do you claim tax-saving investment opportunities available under the National Pension System (NPS)?',
                    options: [
                        { displayName: 'Yes', value: true },
                        { displayName: 'No', value: false },
                    ],
                },
                {
                    id: 'claimHraDeduction',
                    question: 'Do you claim HRA as a deduction?',
                    options: [
                        { displayName: 'Yes', value: true },
                        { displayName: 'No', value: false },
                    ],
                    conditionalRendering: {
                        section: 'basicDetails',
                        question: 'taxRegime',
                        value: 'oldRegime',
                    }
                },
                {
                    id: 'otherIncomeTypes',
                    question: 'Do you have other income in the form of:',
                    subQuestions: [
                        {
                            id: 'rentalIncome',
                            question: 'Rental Income',
                            options: [
                                { displayName: 'Yes', value: true },
                                { displayName: 'No', value: false },
                            ],
                            tooltip: {
                                content : 'Income from renting out property is taxable and allows deductions for maintenance, interest on home loans, and depreciation.',
                                className: 'top-7 xl:top-6 left-1/2 -translate-x-1/3 xl:-translate-x-0',
                            }
                        },
                        {
                            id: 'interestIncome',
                            question: 'Interest Income',
                            options: [
                                { displayName: 'Yes', value: true },
                                { displayName: 'No', value: false },
                            ],
                            tooltip: {
                                content : 'Interest earned from savings accounts, fixed deposits, bonds, and other debt instruments is fully taxable as per your income tax slab.',
                                className: 'top-7 xl:top-6 left-1/2 -translate-x-1/3 xl:-translate-x-0',
                            }
                        },
                        {
                            id: 'dividendIncome',
                            question: 'Dividend Income',
                            options: [
                                { displayName: 'Yes', value: true },
                                { displayName: 'No', value: false },
                            ],
                            tooltip: {
                                content : 'Dividend income from stocks and mutual funds is taxable in your hands at your applicable income tax rate.',
                                className: 'top-7 xl:top-6 left-1/2 -translate-x-1/3 xl:-translate-x-0',
                            }
                        },
                    ],
                },
                {
                    id: 'freelanceIncome',
                    question:
                        'Do you have any income from freelancing, consulting, or other sources?',
                    options: [
                        { displayName: 'Yes', value: true },
                        { displayName: 'No', value: false },
                    ],
                    conditionalRendering: {
                        section: 'basicDetails',
                        question: 'professionType',
                        value: 'salariedProfessional',
                    },
                },
                {
                    id: 'housingLoan',
                    question: 'Do you have a housing loan?',
                    options: [
                        { displayName: 'Yes', value: true },
                        { displayName: 'No', value: false },
                    ],
                },
                {
                    id: 'personalLoan',
                    question: 'Do you have a personal loan?',
                    options: [
                        { displayName: 'Yes', value: true },
                        { displayName: 'No', value: false },
                    ],
                },
            ],
        },
        otherInvestmentProducts: {
            title: 'Other Investment Products',
            description: 'Tell us about your alternative investment portfolio',
            questions: [
                {
                    id: 'hasEsopsOrRsu',
                    question: "Do you have any ESOP'S/RSU?",
                    options: [
                        { displayName: 'Yes', value: true },
                        { displayName: 'No', value: false },
                    ],
                    tooltip: {
                        content : 'Employee Stock Ownership Plans and Restricted Stock Units are equity compensation from employers that have specific tax implications.',
                        className: 'top-7 xl:top-6 left-1/2 -translate-x-[90%] md:-translate-x-1/2 xl:-translate-x-0',
                    }
                },
                {
                    id: 'hasCryptoInvestments',
                    question: 'Do you have investments in Crypto currencies?',
                    options: [
                        { displayName: 'Yes', value: true },
                        { displayName: 'No', value: false },
                    ],
                },
                {
                    id: 'hasReitsOrInvits',
                    question: 'Do you have investments in REITs/InvITs?',
                    options: [
                        { displayName: 'Yes', value: true },
                        { displayName: 'No', value: false },
                    ],
                    tooltip: {
                        content : 'Real Estate Investment Trusts and Infrastructure Investment Trusts are market-linked securities that invest in income-generating real estate and infrastructure assets.',
                        className: `top-7 xl:top-6 left-1/2 ${width < 394 ? ' -translate-x-[10%]' : '-translate-x-[90%]'} md:-translate-x-1/2 xl:-translate-x-0`,
                    }
                },
                {
                    id: 'otherInvestmentTypes',
                    question:
                        "Do you invest in any of the below investment types? <span class='text-tax-insights-solidPink'>Select all that apply</span>",
                    type: 'checkbox',
                    options: [
                        {
                            displayName: 'Debentures/Corporate Debt',
                            value: 'debenturesCorporateDebt',
                        },
                        {
                            displayName: 'Debt Mutual Funds/Liquid Funds',
                            value: 'debtMutualFundsLiquidFunds',
                        },
                        {
                            displayName: 'Partnership Firms',
                            value: 'partnershipFirms',
                        },
                        {
                            displayName: 'Invoice Discounting Product',
                            value: 'invoiceDiscountingProduct',
                        },
                        { displayName: 'None', value: 'none' },
                    ],
                    note: "(Select 'None' if you don't invest in any of the given investment types)",
                },
            ],
        },
        investmentProfiles: {
            title: 'Investment Profile',
            description: 'Tell us about your property ownership and equity portfolio',
            questions: [
                {
                    id: 'ownsHouse',
                    question: 'Do you own a house?',
                    options: [
                        { displayName: 'Yes', value: true },
                        { displayName: 'No', value: false },
                    ],
                },
                {
                    id: 'investsInStockMarket',
                    question: 'Do you invest in the stock market?',
                    options: [
                        { displayName: 'Yes', value: true },
                        { displayName: 'No', value: false },
                    ],
                },
                {
                    id: 'marketLinkedProducts',
                    question:
                        "Do you invest in the following market linked products?  <span class='text-tax-insights-solidPink'>Select all that apply</span>",
                    type: 'checkbox',
                    options: [
                        { displayName: 'Direct Equity', value: 'directEquity' },
                        {
                            displayName: 'Equity Oriented Mutual Funds',
                            value: 'equityOrientedMutualFunds',
                        },
                        {
                            displayName:
                                'Futures and Options or any other Derivatives?',
                            value: 'futuresOptionsDerivatives',
                        },
                        { displayName: 'None', value: 'none' },
                    ],
                    parentQuestion: {
                        id: 'investsInStockMarket',
                        value: true,
                    },
                },
            ],
        },
    };

    return (
        details[slug] || {
            title: 'Default Title',
            description: 'Default Description',
            questions: [],
        }
    );
};

export const getSchemaForSlug = (slug, questionnaire) => {
    const requiredMsg = 'Please select an option';
    const schemas = {
        basicDetails: yup.object().shape({
            professionType: yup
                .string()
                .required(requiredMsg),
            taxRegime: yup.string().required(requiredMsg),
            taxKnowledge: yup.string().required(requiredMsg),
            effectiveTaxRate: yup
                .string()
                .required(requiredMsg),
            usesCA: yup.string().required(requiredMsg),
        }),
        personalProfile: yup.object().shape({
            married: yup.string().required(requiredMsg),
            haveKids: yup.string().required(requiredMsg),
            haveGirlChild: yup.string().nullable().when('haveKids', {
                is: (value) => value === 'true',
                then: (schema) => schema.required(requiredMsg),
                otherwise: (schema) => schema.nullable(),
            }),
            paySchoolFees: yup.string().nullable().when('haveKids', {
                is: (value) => value === 'true',
                then: (schema) => schema.required(requiredMsg),
                otherwise: (schema) => schema.nullable(),
            }),
            planningHigherEducation: yup.string().nullable().when('haveKids', {
                is: (value) => value === 'true',
                then: (schema) => schema.required(requiredMsg),
                otherwise: (schema) => schema.nullable(),
            }),
            otherInvestments: yup.string().required(requiredMsg),
            parentsSeniorCitizens: yup
                .string().test(
                'required-based-on-context',
                requiredMsg,
                function (value) {
                  if (questionnaire?.basicDetails?.taxRegime === 'oldRegime' && !value) {
                    return this.createError({ message: requiredMsg });
                  }
                  return true;
                }
            ),
        }),
        financialProfile: yup.object().shape({
            lifeInsurance: yup.string().test(
                'required-based-on-context',
                requiredMsg,
                function (value) {
                  if (questionnaire?.basicDetails?.taxRegime === 'oldRegime' && !value) {
                    return this.createError({ message: requiredMsg });
                  }
                  return true;
                }
            ),
            medicalInsuranceSelf: yup.string().test(
                'required-based-on-context',
                requiredMsg,
                function (value) {
                  if (questionnaire?.basicDetails?.taxRegime === 'oldRegime' && !value) {
                    return this.createError({ message: requiredMsg });
                  }
                  return true;
                }
            ),
            medicalInsuranceParents: yup.string().test(
                'required-based-on-context',
                requiredMsg,
                function (value) {
                  if (questionnaire?.basicDetails?.taxRegime === 'oldRegime' && !value) {
                    return this.createError({ message: requiredMsg });
                  }
                  return true;
                }
            ),
            section80cDeduction: yup.string().test(
                'required-based-on-context',
                requiredMsg,
                function (value) {
                  if (questionnaire?.basicDetails?.taxRegime === 'oldRegime' && !value) {
                    return this.createError({ message: requiredMsg });
                  }
                  return true;
                }
            ),
            section80cMode: yup.string().nullable().when('section80cDeduction', {
                is: (value) => value === 'true',
                then: (schema) => schema.required(requiredMsg),
                otherwise: (schema) => schema.nullable(),
            }),
            npsInvestment: yup.string().required(requiredMsg),
            claimHraDeduction: yup.string().test(
                'required-based-on-context',
                requiredMsg,
                function (value) {
                  if (questionnaire?.basicDetails?.taxRegime === 'oldRegime' && !value) {
                    return this.createError({ message: requiredMsg });
                  }
                  return true;
                }
            ),
            rentalIncome: yup.string().required(requiredMsg),
            interestIncome: yup.string().required(requiredMsg),
            dividendIncome: yup.string().required(requiredMsg),
            freelanceIncome: yup.string().test(
                'required-based-on-context',
                requiredMsg,
                function (value) {
                  if (questionnaire?.basicDetails?.professionType === 'salariedProfessional' && !value) {
                    return this.createError({ message: requiredMsg });
                  }
                  return true;
                }
            ),
            housingLoan: yup.string().required(requiredMsg),
            personalLoan: yup.string().required(requiredMsg),
        }),
        otherInvestmentProducts: yup.object().shape({
            hasEsopsOrRsu: yup.string().required(requiredMsg),
            hasCryptoInvestments: yup
                .string()
                .required(requiredMsg),
            hasReitsOrInvits: yup.string().required(requiredMsg),
            otherInvestmentTypes: yup
                .string()
                .required(requiredMsg),
        }),
        investmentProfiles: yup.object().shape({
            ownsHouse: yup.string().required(requiredMsg),
            investsInStockMarket: yup
                .string()
                .required(requiredMsg),
            marketLinkedProducts: yup.string().nullable().when('investsInStockMarket', {
                is: (value) => value === 'true',
                then: (schema) => schema.required(requiredMsg),
                otherwise: (schema) => schema.nullable(),
            }),
        }),
    };

    return schemas[slug] || yup.object().shape({});
};

export const getEventNameForSlug = (slug) => {
    switch (slug) {
        case TaxQuestionnaireSlugs.basicDetails:
            return {
                gaEventName: taxOptimiserV2Events.TO_QUESTIONNAIRE_BASIC_PROFILE,
                moengageEventName: moengageEventConstants.TO_QUESTIONNAIRE_BASIC_PROFILE
            };
        case TaxQuestionnaireSlugs.personalProfile:
            return {
                gaEventName: taxOptimiserV2Events.TO_QUESTIONNAIRE_PERSONAL_PROFILE,
                moengageEventName: moengageEventConstants.TO_QUESTIONNAIRE_PERSONAL_PROFILE
            };
        case TaxQuestionnaireSlugs.financialProfile:
            return {
                gaEventName: taxOptimiserV2Events.TO_QUESTIONNAIRE_FINANCE_PROFILE,
                moengageEventName: moengageEventConstants.TO_QUESTIONNAIRE_FINANCE_PROFILE
            };
        case TaxQuestionnaireSlugs.investmentProfiles:
            return {
                gaEventName: taxOptimiserV2Events.TO_QUESTIONNAIRE_INVESTMENT_PROFILE,
                moengageEventName: moengageEventConstants.TO_QUESTIONNAIRE_INVESTMENT_PROFILE
            };
        case TaxQuestionnaireSlugs.otherInvestmentProducts:
            return {
                gaEventName: taxOptimiserV2Events.TO_QUESTIONNAIRE_OTHER_INVESTMENT,
                moengageEventName: moengageEventConstants.TO_QUESTIONNAIRE_OTHER_INVESTMENT
            };
        default:
            return { gaEventName: '', moengageEventName: '' };
    }
};
